import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, FormControl, InputLabel, Select, MenuItem, FormHelperText, Button, CircularProgress, Box, Alert, 
    Dialog, DialogTitle, DialogContent, DialogActions, Typography, Link, InputAdornment, IconButton } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { 
  useSalonsListQuery, 
  useAppointmentsCreateMutation, 
  useAuthProfileRetrieveQuery,
  useTeamsTeamMembersByServiceSalonRetrieveQuery,
  useServicesSalonListQuery,
  useUsersClientsBySalonListQuery,
  useSalonsRetrieveQuery,
  useSchedulesSchedulesByTeamMemberRetrieveQuery,
  useAppointmentsAvailableSlotsRetrieveQuery
} from '../../../services/GLOWClient';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import CustomTimePicker from '../../../SystemDesign/CustomTimePicker';
import EventIcon from '@mui/icons-material/Event';
import { format, getDay, isAfter, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import apiService from '../../../services/api.service';

const AddAppointment = ({ open, onClose, onSuccess, prefilledData = {}, hideStatusField = false }) => {
    const { t } = useTranslation();

    
    const [formData, setFormData] = useState({
        // Default values
        salon: '',
        client: null,
        team_member: null,
        service: null,
        start_time: new Date(),
        end_time: new Date(new Date().getTime() + 60 * 60 * 1000), // Default 1 hour later
        status: 'confirmed',
        // Override with prefilled data if provided
        ...prefilledData
    });
    
    const [errors, setErrors] = useState({});
    const [dialogClosed, setDialogClosed] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    
    // Client search state with debounce
    const [clientSearchTerm, setClientSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [startTimePickerOpen, setStartTimePickerOpen] = useState(false);
    const [endTimePickerOpen, setEndTimePickerOpen] = useState(false);
    
    // Initialisation du hook pour créer un rendez-vous
    const [createAppointment, { isLoading: isCreating }] = useAppointmentsCreateMutation();
    
    // Get current user profile to filter salons by owner
    const { data: userProfile, isLoading: profileLoading } = useAuthProfileRetrieveQuery();
    
    // Fetch salons owned by the current user - seulement après chargement du profil
    const { data: salonsData, isLoading: salonsLoading } = useSalonsListQuery(
        userProfile && userProfile.id ? { owner: userProfile.id } : { skip: true }
    );
    
    // Fetch salon details when a salon is selected - Skip COMPLETELY if no salon
    const salonIdForAPI = formData.salon && formData.salon !== '' && formData.salon !== undefined 
        ? formData.salon 
        : null;
    
    const { data: salonDetails, isLoading: salonDetailsLoading } = useSalonsRetrieveQuery(
        { salonId: salonIdForAPI }, 
        { skip: !salonIdForAPI }
    );
    
    // Fetch only clients who already have appointments in the selected salon.
    const { data: clientsData, isLoading: clientsLoading, error: clientsError } = useUsersClientsBySalonListQuery(
        salonIdForAPI
            ? {
                salonId: Number(salonIdForAPI),
                ...(clientSearchTerm ? { search: clientSearchTerm } : {})
            }
            : undefined,
        { skip: !salonIdForAPI }
    );
    
    // Handle client search input change with debounce
    const handleClientSearchChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        
        // Clear previous timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        
        // Set new timeout for debounce (500ms)
        const newTimeout = setTimeout(() => {
            setClientSearchTerm(value);
        }, 500);
        
        setSearchTimeout(newTimeout);
    };
    
    // Ajouter des logs de diagnostic pour l'authentification et les appels API
    useEffect(() => {
        // Vérifier l'état d'authentification
        const accessToken = localStorage.getItem('access_token');
        const userStr = localStorage.getItem('user');
        let userToken = null;
        
        if (userStr) {
            try {
                const userData = JSON.parse(userStr);
                userToken = userData.token;
            } catch (e) {
                console.error('Erreur lors du parsing des données utilisateur:', e);
            }
        }
       
        
        if (clientsError) {
            console.error('Détails de l\'erreur clients:', clientsError);
        }
    }, [clientsLoading, clientsError]);
    
   
    const [clients, setClients] = useState([]);
    const [clientsStateLoading, setClientsStateLoading] = useState(false);
    const [clientsStateError, setClientsStateError] = useState(null);
    
    // Sync clients from API response to local state
    useEffect(() => {
        if (clientsData) {
            const clientsList = Array.isArray(clientsData) ? clientsData : [];
            setClients(clientsList);
            
            // If there's a prefilled client, try to find it in the results
            if (prefilledData.client && clientsList.length > 0) {
                const foundClient = clientsList.find(c => c.id === prefilledData.client);
                if (foundClient) {
                    setFormData(prev => ({
                        ...prev,
                        client: foundClient.id
                    }));
                }
            }
        } else if (!salonIdForAPI) {
            setClients([]);
        }
    }, [clientsData, prefilledData.client, salonIdForAPI]);
    
    // État pour la modale d'ajout de client
    const [openClientModal, setOpenClientModal] = useState(false);
    const [newClientData, setNewClientData] = useState({
        first_name: '',
        last_name: '',
        phone: '',
        email: ''
    });
    const [clientErrors, setClientErrors] = useState({});
    const [isCreatingClient, setIsCreatingClient] = useState(false);
    
    // Fetch team members for the selected salon and service - Skip COMPLETELY if no salon or service
    const salonIdForTeamMembers = formData.salon && formData.salon !== '' && formData.salon !== undefined 
        ? formData.salon 
        : null;
    const serviceIdForTeamMembers = formData.service && formData.service !== '' && formData.service !== undefined
        ? formData.service
        : null;
    
    const { data: filteredTeamMembers, isLoading: filteredTeamMembersLoading, isFetching: filteredTeamMembersFetching } = 
    useTeamsTeamMembersByServiceSalonRetrieveQuery(
        { salon: salonIdForTeamMembers, service: serviceIdForTeamMembers },
        { skip: !salonIdForTeamMembers || !serviceIdForTeamMembers }
    );

    // Fetch team member's schedule when selected - Skip COMPLETELY if no team member
    const teamMemberIdForSchedule = formData.team_member && formData.team_member !== '' && formData.team_member !== undefined
        ? formData.team_member
        : null;
    
    const { data: teamMemberSchedule, isLoading: scheduleLoading } = useSchedulesSchedulesByTeamMemberRetrieveQuery(
        { teamMemberId: teamMemberIdForSchedule },
        { skip: !teamMemberIdForSchedule }
    );

    // Fetch services for the selected salon
    const salonIdForServices = formData.salon && formData.salon !== '' && formData.salon !== undefined 
        ? formData.salon 
        : null;
    
    const { data: servicesData, isLoading: servicesLoading } = useServicesSalonListQuery(
        salonIdForServices ? { salonId: salonIdForServices } : undefined,
        { skip: !salonIdForServices }
    );

    // Réinitialiser l'employé sélectionné quand le service change (but not when prefilled data is being set)
    useEffect(() => {
        if (formData.service && formData.team_member && !prefilledData.team_member) {
            setFormData(prev => ({
                ...prev,
                team_member: ''
            }));
        }
    }, [formData.service, prefilledData.team_member]);

    // Fetch available slots when service and team member are selected
    const [availableSlots, setAvailableSlots] = useState([]);
    const [fetchingSlots, setFetchingSlots] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    
    // Create query parameters for available slots
    const availableSlotsParams = formData.service && formData.team_member && selectedDate ? {
        serviceId: formData.service,
        teamMemberId: formData.team_member,
        date: format(selectedDate, 'yyyy-MM-dd')
    } : null;
    
    // Use the RTK Query hook to fetch available slots
    const { 
        data: availableSlotsData, 
        isLoading: isLoadingSlots,
        error: slotsError,
        refetch: refetchAvailableSlots
    } = useAppointmentsAvailableSlotsRetrieveQuery(availableSlotsParams, {
        // Skip the query if we don't have the required parameters
        skip: !formData.service || !formData.team_member || !selectedDate,
        // Refetch when the date changes
        refetchOnMountOrArgChange: true
    });
    
    // Update available slots when data is received
    useEffect(() => {
        if (availableSlotsData) {
            setAvailableSlots(availableSlotsData.available_slots || []);
        }
    }, [availableSlotsData]);
    
    // Update loading state
    useEffect(() => {
        setFetchingSlots(isLoadingSlots);
    }, [isLoadingSlots]);
    
    // Log any errors
    useEffect(() => {
        if (slotsError) {
            console.error('Error fetching available slots:', slotsError);
        }
    }, [slotsError]);

    // Auto-refetch slots when dependencies change (service, team member, selectedDate)
    useEffect(() => {
        if (refetchAvailableSlots && formData.service && formData.team_member && selectedDate) {
            refetchAvailableSlots();
        }
    }, [formData.service, formData.team_member, selectedDate]);

    // Memoize selected service for performance
    const selectedService = useMemo(() => {
        return servicesData?.find(service => service.service_id === Number(formData.service));
    }, [formData.service, servicesData]);

    // Automatically calculate end time based on service duration when service or start time changes
    useEffect(() => {
        if (formData.service && formData.start_time && selectedService) {
            // Create a new date object to avoid reference issues
            const startTime = new Date(formData.start_time);
            
            // Calculate end time based on service duration (without rounding start_time)
            const endTime = new Date(startTime);
            endTime.setMinutes(endTime.getMinutes() + selectedService.duration);
            
            // Only update end_time if it has actually changed to avoid infinite loops
            setFormData(prev => {
                // Check if the end_time would actually change
                if (prev.end_time?.getTime() !== endTime.getTime()) {
                    return {
                        ...prev,
                        end_time: endTime
                    };
                }
                return prev;
            });
        }
    }, [formData.service, formData.start_time?.getTime(), selectedService?.duration]);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Si le salon change, réinitialiser service et team_member
        if (name === 'salon') {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                client: null,
                service: '', // Réinitialiser le service sélectionné
                team_member: '' // Réinitialiser l'employé sélectionné
            }));
            setSearchInput('');
            setClientSearchTerm('');
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        
        // Clear error when field is edited
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Memoize the rounding function
    const roundToNearest15Min = useCallback((date) => {
        const newDate = new Date(date);
        const minutes = newDate.getMinutes();
        const roundedMinutes = Math.round(minutes / 15) * 15;
        newDate.setMinutes(roundedMinutes, 0, 0);
        return newDate;
    }, []);

    const handleDateChange = useCallback((name, value) => {
        if (!value) return;
        
        let newDate;
        if (typeof value === 'string') {
            // Handle DD/MM/YYYY HH:mm format
            const dateTimeRegex = /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/;
            const match = value.match(dateTimeRegex);
            
            if (match) {
                const [, day, month, year, hour, minute] = match;
                // Create date with correct format (month is 0-indexed in JS)
                newDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
            } else {
                // Handle Date object or other formats
                newDate = new Date(value);
            }
        } else {
            // Handle Date object
            newDate = new Date(value);
        }
        
        if (isNaN(newDate.getTime())) return; // Invalid date
        
        // Store the exact time without rounding for user flexibility
        setFormData(prev => {
            const updatedData = {
                ...prev,
                [name]: newDate
            };
            
            // If start time changed, update end time based on service duration
            if (name === 'start_time' && selectedService) {
                const endTime = new Date(newDate);
                endTime.setMinutes(endTime.getMinutes() + selectedService.duration);
                updatedData.end_time = endTime;
            }
            
            return updatedData;
        });
        
        // Clear any previous errors
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    }, [selectedService, roundToNearest15Min, errors]);

    const formatDateForAPI = (date) => {
        // Ensure we're working with a Date object
        const d = new Date(date);
        // Format as YYYY-MM-DDTHH:mm:ss (local time, no timezone) - backend will handle validation
        const pad = (num) => num.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
    };

    const isTimeSlotAvailable = (time, availableSlots) => {
        if (!availableSlots?.length) return false;
        
        const timeStr = format(time, 'HH:mm');
        return availableSlots.includes(timeStr);
    };

    const handleSubmit = async () => {
        // Check if all required fields are filled
        if (!formData.salon || !formData.client || !formData.service || !formData.team_member || !formData.start_time || !formData.end_time) {
            setValidationMessage(t('pro_appointments.validation.fill_required_fields'));
            return;
        }
        
        // Validate the selected time slot
        // if (!isTimeSlotAvailable(formData.start_time, availableSlots)) {
        //     setErrors(prev => ({
        //         ...prev,
        //         start_time: 'Veuillez sélectionner un créneau horaire disponible dans la liste'
        //     }));
        //     return;
        // }
        
        try {
            // Get the selected service to verify duration
            const selectedService = servicesData?.find(
                s => s.service_id === Number(formData.service)
            );
            
            if (!selectedService) {
                setValidationMessage(t('pro_appointments.error.service_not_found'));
                return;
            }
            
            // Format the data according to the API requirements
            const createData = {
                appointmentCreateRequest: {
                    salon: Number(formData.salon),
                    client: formData.client ? Number(formData.client) : null,
                    team_member: formData.team_member ? Number(formData.team_member) : null,
                    service: formData.service ? Number(formData.service) : null,
                    start_time: formatDateForAPI(new Date(formData.start_time)),
                    end_time: formatDateForAPI(new Date(formData.end_time)),
                    status: formData.status,
                    // Add service duration for backend validation
                    duration: selectedService.duration
                }
            };
            
            
            const result = await createAppointment(createData).unwrap();
            
            // Explicitly refetch available slots to update the UI
            if (refetchAvailableSlots) {
                refetchAvailableSlots();
            }
            
            // Reset form data
            setFormData({
                salon: '',
                client: null,
                team_member: null,
                service: null,
                start_time: new Date(),
                end_time: new Date(new Date().getTime() + 60 * 60 * 1000),
                status: 'confirmed'
            });
            
            // Call success callback and close dialog
            onSuccess && onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to create appointment:', error);
          
            // Handle API validation errors
            if (error.data && error.data.errors) {
                setErrors(error.data.errors);
            } else if (error.data) {
                // Handle non-field errors or other error formats
                const apiErrors = {};
                
                if (error.data.salon) apiErrors.salon = error.data.salon.join(' ');
                if (error.data.client) apiErrors.client = error.data.client.join(' ');
                if (error.data.team_member) apiErrors.team_member = error.data.team_member.join(' ');
                if (error.data.service) apiErrors.service = error.data.service.join(' ');
                if (error.data.start_time) {
                    apiErrors.start_time = error.data.start_time.join(' ');
                }
                if (error.data.end_time) apiErrors.end_time = error.data.end_time.join(' ');
                if (error.data.status) apiErrors.status = error.data.status.join(' ');
                if (error.data.non_field_errors) {
                    // Show non-field errors in a visible field
                    apiErrors.salon = error.data.non_field_errors.join(' ');
                }
                
                setErrors(apiErrors);
            }
        }
    };
    
    // Handle prefilled data when dialog opens
    useEffect(() => {
        if (open && prefilledData && Object.keys(prefilledData).length > 0) {
            setFormData(prev => ({
                ...prev,
                ...prefilledData
            }));
            // Initialize selectedDate from prefilled start_time if provided, else today
            if (prefilledData.start_time) {
                try {
                    const d = new Date(prefilledData.start_time);
                    if (!isNaN(d.getTime())) setSelectedDate(d);
                } catch {}
            } else {
                setSelectedDate(new Date());
            }
        }
    }, [open, prefilledData]);

    // Reset form and close dialog when onClose is called
    useEffect(() => {
        if (open === false) {
            setDialogClosed(true);
            // Reset form data when dialog is closed
            setFormData({
                salon: '',
                client: null,
                team_member: null,
                service: null,
                start_time: new Date(),
                end_time: new Date(new Date().getTime() + 60 * 60 * 1000),
                status: 'pending'
            });
            setErrors({});
            setSelectedDate(new Date());
        } else {
            setDialogClosed(false);
        }
    }, [open]);

    // Handlers for client modal
    const handleOpenClientModal = () => {
        setOpenClientModal(true);
    };
    
    const handleCloseClientModal = () => {
        setOpenClientModal(false);
        setNewClientData({
            first_name: '',
            last_name: '',
            phone: '',
            email: ''
        });
        setClientErrors({});
    };
    
    const handleClientInputChange = (e) => {
        const { name, value } = e.target;
        setNewClientData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when field is edited
        if (clientErrors[name]) {
            setClientErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };
    
    const validateClientForm = () => {
        const newErrors = {};
        
        if (!newClientData.first_name.trim()) newErrors.first_name = t('pro_appointments.validation.first_name_required');
        if (!newClientData.last_name.trim()) newErrors.last_name = t('pro_appointments.validation.last_name_required');
        if (!newClientData.phone.trim()) newErrors.phone = t('pro_appointments.validation.phone_required');
        if (newClientData.phone && !/^[+]?[0-9]{1,15}$/.test(newClientData.phone)) {
            newErrors.phone = t('pro_appointments.validation.phone_invalid');
        }
        if (newClientData.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(newClientData.email)) {
            newErrors.email = t('pro_appointments.validation.email_invalid');
        }
        
        setClientErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleCreateClient = async () => {
        if (!validateClientForm()) return;
        
        setIsCreatingClient(true);
        
        try {
            // Utiliser la nouvelle API addclient au lieu de register
            const response = await apiService.addClient({
                first_name: newClientData.first_name,
                last_name: newClientData.last_name,
                phone: newClientData.phone,
                email: newClientData.email // optionnel
            });
            
            const newClient = response.data;
            
            // Ajouter le nouveau client à la liste des clients
            setClients(prev => [...prev, newClient]);
            
            // Sélectionner automatiquement le nouveau client
            setFormData(prev => ({
                ...prev,
                client: newClient.id
            }));
            
            handleCloseClientModal();
            
        } catch (error) {
            console.error('Erreur lors de la création du client:', error);
            if (error.response && error.response.data) {
                // Gérer les erreurs spécifiques de l'API
                const apiErrors = {};
                Object.entries(error.response.data).forEach(([key, value]) => {
                    apiErrors[key] = Array.isArray(value) ? value[0] : value;
                });
                setClientErrors(apiErrors);
            } else {
                setClientErrors({ general: t('pro_appointments.error.client_creation_failed') });
            }
        } finally {
            setIsCreatingClient(false);
        }
    };

    // Memoize filtered available slots for performance - only recalculate when slots or date change significantly
    const filteredAvailableSlots = useMemo(() => {
        if (!availableSlots?.length || !selectedDate) return [];
        const today = new Date();
        const baseDate = new Date(selectedDate);
        
        return availableSlots
            .filter(slot => {
                // Filter out past hours for today
                const [hours, minutes] = slot.split(':');
                const slotDate = new Date(baseDate);
                slotDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                
                // If it's today, only show future slots
                const isToday = baseDate.toDateString() === today.toDateString();
                
                if (isToday) {
                    return slotDate > today;
                }
                
                // For future dates, show all slots
                return true;
            })
            .map((slot, index) => {
                const [hours, minutes] = slot.split(':');
                const slotDate = new Date(baseDate);
                slotDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
                return { slot, slotDate, index };
            });
    }, [availableSlots, selectedDate?.toDateString()]);

    // Memoize slot button click handler
    const handleSlotClick = useCallback((slotInfo) => {
        handleDateChange('start_time', slotInfo.slotDate);
    }, [handleDateChange]);

    // Memoize refetch with timeout - only refetch when date actually changes
    const debouncedRefetch = useCallback((newDate) => {
        if (refetchAvailableSlots && newDate) {
            const timeoutId = setTimeout(() => {
                refetchAvailableSlots();
            }, 300);
            return () => clearTimeout(timeoutId);
        }
    }, [refetchAvailableSlots]);

    // Handle calendar icon click to open CustomTimePicker
    const handleCalendarClick = useCallback((field) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        if (field === 'start_time') {
            setStartTimePickerOpen(true);
        } else {
            setEndTimePickerOpen(true);
        }
    }, []);

    // Handle time selection from CustomTimePicker
    const handleTimeSelect = useCallback((field) => (date) => {
        // Store the exact time without rounding for user flexibility
        setFormData(prev => {
            const updatedData = {
                ...prev,
                [field]: date
            };
            
            // If start time changed, update end time based on service duration
            if (field === 'start_time' && selectedService) {
                const endTime = new Date(date);
                endTime.setMinutes(endTime.getMinutes() + selectedService.duration);
                updatedData.end_time = endTime;
            }
            
            return updatedData;
        });
        
        // Clear any previous errors
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
        
        // Refetch available slots if this is start_time
        if (field === 'start_time') {
            debouncedRefetch(date);
        }
    }, [selectedService, errors, debouncedRefetch]);

    // Form content to be displayed in the dialog
    const formContent = (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {validationMessage && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {validationMessage}
                    </Alert>
                )}
                
                {/* Display API validation errors prominently */}
                {Object.keys(errors).length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {t('pro_appointments.validation.errors_title')}:
                        </Typography>
                        {errors.start_time && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{t('pro_appointments.labels.start_time')}:</strong> {errors.start_time}
                            </Typography>
                        )}
                        {errors.end_time && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{t('pro_appointments.labels.end_time')}:</strong> {errors.end_time}
                            </Typography>
                        )}
                        {errors.service && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{t('pro_appointments.labels.service')}:</strong> {errors.service}
                            </Typography>
                        )}
                        {errors.team_member && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{t('pro_appointments.labels.team_member')}:</strong> {errors.team_member}
                            </Typography>
                        )}
                        {errors.client && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{t('pro_appointments.labels.client')}:</strong> {errors.client}
                            </Typography>
                        )}
                        {errors.salon && !errors.start_time && !errors.end_time && !errors.service && !errors.team_member && !errors.client && (
                            <Typography variant="body2">
                                <strong>{t('pro_appointments.labels.salon')}:</strong> {errors.salon}
                            </Typography>
                        )}
                    </Alert>
                )}
                {/* Salon Selection or Display */}
                {prefilledData.salon ? (
                    <TextField
                        fullWidth
                        margin="normal"
                        label={t('pro_appointments.labels.salon')}
                        value={salonDetails?.name || t('common.loading')}
                        disabled
                    />
                ) : (
                    <FormControl 
                        fullWidth 
                        margin="normal"
                        error={!!errors.salon}
                        disabled={isCreating || salonsLoading || profileLoading}
                    >
                        <InputLabel>{t('pro_appointments.labels.salon')}</InputLabel>
                        <Select
                            name="salon"
                            value={formData.salon}
                            onChange={handleChange}
                            label={t('pro_appointments.labels.salon')}
                        >
                            {salonsLoading ? (
                                <MenuItem disabled>{t('common.loading')}</MenuItem>
                            ) : salonsData && salonsData.length > 0 ? (
                                salonsData.map(salon => (
                                    <MenuItem key={salon.salon_id} value={salon.salon_id}>
                                        {salon.name}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem disabled>{t('pro_appointments.no_salons')}</MenuItem>
                            )}
                        </Select>
                        {errors.salon && <FormHelperText>{errors.salon}</FormHelperText>}
                    </FormControl>
                )}

               {/* Client Search and Selection */}
<FormControl 
  fullWidth 
  margin="normal"
  error={!!errors.client}
  disabled={isCreating}
  sx={{
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#e0e0e0'
      },
      '&:hover fieldset': {
        borderColor: '#ccc'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1976d2'
      }
    },
    '& .MuiInputLabel-root': {
      backgroundColor: 'white',
      paddingLeft: '4px',
      paddingRight: '4px'
    }
  }}
>
  <InputLabel shrink={!!formData.client}>Client</InputLabel>
  <Select
    name="client"
    value={formData.client || ''}
    onChange={(e) => {
        handleChange(e);
        // Clear search when a client is selected
        setSearchInput('');
        setClientSearchTerm('');
    }}
    onOpen={() => {
        setClientSearchTerm('');
        setSearchInput('');
        setIsClientDropdownOpen(true);
    }}
    onClose={() => setIsClientDropdownOpen(false)}
    open={isClientDropdownOpen}
    label={t('pro_appointments.labels.client')}
    displayEmpty
    notched={!!formData.client}
    // Disable keyboard navigation to prevent interference with search
    onKeyDown={(e) => {
      // Prevent default Select keyboard navigation
      e.preventDefault();
      e.stopPropagation();
    }}
    renderValue={(selected) => {
        if (!selected) return '';
        // Find the selected client in the current results or the full list
        const selectedClient = clients?.find(c => c.id === selected);
        return selectedClient 
            ? `${selectedClient.first_name} ${selectedClient.last_name}`
            : t('pro_appointments.placeholders.select_client');
    }}
  >
    <Box sx={{ p: 1 }}>
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder={t('pro_appointments.search_client_placeholder')}
        value={searchInput}
        onChange={handleClientSearchChange}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          // Stop propagation to prevent Select from handling keyboard events
          e.stopPropagation();
        }}
        autoFocus
        autoComplete="off"
      />
    </Box>
    {clientsLoading ? (
      <MenuItem disabled>{t('pro_appointments.loading.clients')}</MenuItem>
    ) : clientsError ? (
      <MenuItem disabled>{t('pro_appointments.error.loading_clients')}</MenuItem>
    ) : clients && clients.length > 0 ? (
      clients.map(client => (
        <MenuItem 
          key={client.id} 
          value={client.id}
          onClick={() => {
            setFormData(prev => ({
              ...prev,
              client: client.id
            }));
            setIsClientDropdownOpen(false);
          }}
        >
          {client.first_name} {client.last_name} {client.email ? `(${client.email})` : ''}
        </MenuItem>
      ))
    ) : (
      <MenuItem disabled>
        {clientSearchTerm ? t('pro_appointments.no_clients_found') : t('pro_appointments.start_search')}
      </MenuItem>
    )}
  </Select>
  {errors.client && <FormHelperText>{errors.client}</FormHelperText>}
</FormControl>
                
                {/* Bouton pour ajouter un nouveau client */}
                <Box sx={{ mt: -1, mb: 1 }}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={handleOpenClientModal}
                        sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                    >
                        <Typography variant="body2" color="primary">
                            + {t('pro_appointments.add_client')}
                        </Typography>
                    </Link>
                </Box>

                {/* Bouton pour recharger les clients */}
                {/* <Box sx={{ mt: -1, mb: 1 }}>
                    <Button
                        variant="contained"
                        onClick={refetchClients}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <Typography variant="body2" color="primary">
                            Recharger les clients
                        </Typography>
                    </Button>
                </Box> */}

              

                {/* Service Selection */}
                <FormControl 
                    fullWidth 
                    margin="normal"
                    error={!!errors.service}
                    disabled={isCreating || servicesLoading || !formData.salon}
                >
                    <InputLabel>{t('pro_appointments.labels.service')}</InputLabel>
                    <Select
                        name="service"
                        value={formData.service || ''}
                        onChange={handleChange}
                        label={t('pro_appointments.labels.service')}
                    >
                        <MenuItem value="">{t('pro_appointments.placeholders.select_service')}</MenuItem>
                        {servicesLoading ? (
                            <MenuItem disabled>{t('pro_appointments.loading.services')}</MenuItem>
                        ) : servicesData && servicesData.length > 0 ? (
                            servicesData.map(service => (
                                <MenuItem key={service.service_id} value={service.service_id}>
                                    {service.name} - {service.duration} min
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem disabled>{t('pro_appointments.no_services')}</MenuItem>
                        )}
                    </Select>
                    {errors.service && <FormHelperText>{errors.service}</FormHelperText>}
                </FormControl>
  {/* Team Member Selection */}
  <FormControl 
                    fullWidth 
                    margin="normal"
                    error={!!errors.team_member}
                    disabled={isCreating || filteredTeamMembersLoading || !formData.salon || !formData.service}
                >
                    <InputLabel>{t('pro_appointments.labels.team_member')}</InputLabel>
                    <Select
                        name="team_member"
                        value={formData.team_member || ''}
                        onChange={handleChange}
                        label={t('pro_appointments.labels.team_member')}
                    >
                        <MenuItem value="">{t('pro_appointments.placeholders.select_team_member')}</MenuItem>
                        {filteredTeamMembersLoading || filteredTeamMembersFetching ? (
                            <MenuItem disabled>{t('pro_appointments.loading.team_members')}</MenuItem>
                        ) : filteredTeamMembers && filteredTeamMembers.length > 0 ? (
                            filteredTeamMembers.map(member => (
                                <MenuItem key={member.team_member_id} value={member.team_member_id}>
                                    {member.user_details?.first_name || ''} {member.user_details?.last_name || ''}
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem disabled>
                                {formData.service ? t('pro_appointments.no_team_members_for_service') : t('pro_appointments.select_service_first')}
                            </MenuItem>
                        )}
                    </Select>
                    {errors.team_member && <FormHelperText>{errors.team_member}</FormHelperText>}
                </FormControl>
                {/* Start Time */}
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    {t('pro_appointments.labels.start_time')}
                  </Typography>
                  <TextField
                    label={t('pro_appointments.labels.select_date')}
                    type="date"
                    value={selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      const d = v ? new Date(v + 'T00:00:00') : null;
                      setSelectedDate(d);
                      // reset previously selected slot times
                      setFormData(prev => ({ ...prev, start_time: null, end_time: null }));
                    }}
                    fullWidth
                    margin="normal"
                    disabled={isCreating || !formData.team_member || !formData.service}
                    helperText={t('pro_appointments.helper.select_date_then_slot')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      min: format(new Date(), 'yyyy-MM-dd')
                    }}
                  />
                  
                  {/* Available time slots */}
                  {filteredAvailableSlots.length > 0 && (
                    <Box sx={{ mt: 1, display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' } }}>
                      {filteredAvailableSlots.map(({ slot, slotDate, index }) => (
                        <Button
                          key={index}
                          variant={formData.start_time?.getHours() === slotDate.getHours() && 
                                 formData.start_time?.getMinutes() === slotDate.getMinutes() 
                                 ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => handleSlotClick({ slot, slotDate, index })}
                          sx={{ width: '100%' }}
                        >
                          {slot}
                        </Button>
                      ))}
                    </Box>
                  )}
                  {formData.service && formData.team_member && selectedDate && fetchingSlots && (
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                      {t('pro_appointments.loading_slots')}
                    </Typography>
                  )}
                  {formData.service && formData.team_member && selectedDate && !fetchingSlots && slotsError && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      {t('pro_appointments.no_slots_available')}
                    </Alert>
                  )}
                  {formData.service && formData.team_member && selectedDate && !fetchingSlots && !slotsError && availableSlotsData && filteredAvailableSlots.length === 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {t('pro_appointments.no_slots_available')}
                    </Alert>
                  )}
                </Box>
                
                {/* End Time - Hidden as requested */}
                {/* End time will be calculated automatically based on service duration */}
                
                {/* Status Selection - Only show if not hidden */}
                {!hideStatusField && (
                    <FormControl 
                        fullWidth 
                        margin="normal"
                        error={!!errors.status}
                        disabled={isCreating}
                    >
                        <InputLabel>{t('pro_appointments.labels.status')}</InputLabel>
                        <Select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            label={t('pro_appointments.labels.status')}
                        >
                            <MenuItem value="pending">{t('pro_appointments.status.pending')}</MenuItem>
                            <MenuItem value="confirmed">{t('pro_appointments.status.confirmed')}</MenuItem>
                            <MenuItem value="cancelled">{t('pro_appointments.status.cancelled')}</MenuItem>
                            <MenuItem value="completed">{t('pro_appointments.status.completed')}</MenuItem>
                        </Select>
                        {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
                    </FormControl>
                )}
            </Box>
    );

    // Trigger button for the dialog - this is hidden as we're controlling the dialog externally
    const triggerButton = <Button style={{ display: 'none' }}>Open</Button>;

    return (
        <>
            <MuiDialog
                open={open}
                icon={<EventIcon />}
                triggerButton={triggerButton}
                title={t('pro_appointments.dialog.title')}
                subtitle={t('pro_appointments.dialog.subtitle')}
                content={formContent}
                existAction={true}
                actionTitle={t('pro_appointments.dialog.create_button')}
                actionFunction={handleSubmit}
                onClose={dialogClosed}
                onExternalClose={onClose}
                isLoading={isCreating}
                actionType="button"
                maxWidth="sm"
                disableAction={isCreating || !formData.salon || !formData.client || !formData.service || !formData.team_member || !formData.start_time || !formData.end_time}
            />
            
            {/* Modale pour ajouter un nouveau client */}
            <Dialog open={openClientModal} onClose={handleCloseClientModal} maxWidth="sm" fullWidth>
                <DialogTitle>{t('pro_appointments.client_modal.title')}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        {clientErrors.general && (
                            <Alert severity="error">{clientErrors.general}</Alert>
                        )}
                        <TextField
                            fullWidth
                            label={t('pro_appointments.client_modal.first_name')}
                            name="first_name"
                            value={newClientData.first_name}
                            onChange={handleClientInputChange}
                            error={!!clientErrors.first_name}
                            helperText={clientErrors.first_name}
                            required
                        />
                        <TextField
                            fullWidth
                            label={t('pro_appointments.client_modal.last_name')}
                            name="last_name"
                            value={newClientData.last_name}
                            onChange={handleClientInputChange}
                            error={!!clientErrors.last_name}
                            helperText={clientErrors.last_name}
                            required
                        />
                        <TextField
                            fullWidth
                            label={t('pro_appointments.client_modal.phone')}
                            name="phone"
                            value={newClientData.phone}
                            onChange={handleClientInputChange}
                            error={!!clientErrors.phone}
                            helperText={clientErrors.phone || t('pro_appointments.client_modal.phone_helper')}
                            required
                        />
                        <TextField
                            fullWidth
                            label={t('pro_appointments.client_modal.email')}
                            name="email"
                            type="email"
                            value={newClientData.email}
                            onChange={handleClientInputChange}
                            error={!!clientErrors.email}
                            helperText={clientErrors.email}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseClientModal}>{t('common.cancel')}</Button>
                    <Button 
                        onClick={handleCreateClient} 
                        variant="contained" 
                        disabled={isCreatingClient}
                    >
                        {isCreatingClient ? <CircularProgress size={24} /> : t('common.add')}
                    </Button>
                </DialogActions>
            </Dialog>
            
            <CustomTimePicker
                open={startTimePickerOpen}
                onClose={() => setStartTimePickerOpen(false)}
                onSelect={handleTimeSelect('start_time')}
                initialValue={formData.start_time}
            />
            
            <CustomTimePicker
                open={endTimePickerOpen}
                onClose={() => setEndTimePickerOpen(false)}
                onSelect={handleTimeSelect('end_time')}
                initialValue={formData.end_time}
            />
        </>
    );
};

export default AddAppointment;
